import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import API from "../api";

const SettingsContext = createContext({
  settings: {},
  ready: false,
  updateSettings: () => {},
});

export const useSettings = () => useContext(SettingsContext);

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState({});
  const [ready, setReady] = useState(false);
  // Keep a ref in sync so callbacks always see latest settings without stale closure
  const settingsRef = useRef({});
  // Serializes saves so concurrent updates can't arrive at the server out of order
  const saveQueueRef = useRef(Promise.resolve());

  useEffect(() => {
    API.getSettings()
      .then((res) => {
        const s = res.data?.settings ?? {};
        settingsRef.current = s;
        setSettings(s);
      })
      .catch(() => {})
      .finally(() => setReady(true));
  }, []);

  // Merges one top-level key and persists the whole settings object.
  // Saves are chained through saveQueueRef so only one request is ever in flight,
  // always carrying the latest settingsRef.current at send time — this prevents an
  // older save from arriving after (and overwriting) a newer one on the server.
  const updateSettings = useCallback((key, value) => {
    const next = { ...settingsRef.current, [key]: value };
    settingsRef.current = next;
    setSettings(next);
    saveQueueRef.current = saveQueueRef.current
      .then(() => API.saveSettings(settingsRef.current))
      .catch(() => {});
    return saveQueueRef.current;
  }, []);

  return (
    <SettingsContext.Provider value={{ settings, ready, updateSettings }}>
      {children}
    </SettingsContext.Provider>
  );
}
